import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import toast from 'react-hot-toast';
import { NAV_GROUPS, MOBILE_NAV_ITEMS } from '../utils/navItems';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LogOut, 
  Menu, 
  X, 
  Search, 
  Bell,
  ChevronLeft,
  ChevronRight,
  Cloud,
  Sun,
  Moon,
  Sparkles
} from 'lucide-react';
import { fadeUp, staggerContainer, staggerItem } from '../utils/motion';

export default function Layout() {
  const { user, logout, storageType } = useAuth();
  const { theme, toggle: toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleLogout = async () => {
    await logout();
    toast.success('Signed out successfully');
    navigate('/');
  };

  // Storage sync badge
  const SyncBadge = () => {
    if (storageType === 'google_drive' && user?.driveConnected) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            background: 'rgba(16, 185, 129, 0.12)',
            borderRadius: '8px',
            marginBottom: '12px',
          }}
        >
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#10b981',
            }}
          />
          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#10b981', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Live Sync
          </span>
          <Cloud size={14} style={{ color: '#10b981' }} />
        </motion.div>
      );
    }
    return null;
  };

  const sidebarWidth = sidebarCollapsed ? 80 : 280;

  return (
    <div className="app-shell">
      {/* Mobile Overlay */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setSidebarOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(3, 7, 18, 0.80)',
              backdropFilter: 'blur(8px)',
              zIndex: 299,
            }}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.nav
        initial={false}
        animate={{ 
          x: isMobile ? (sidebarOpen ? 0 : '-100%') : 0,
          width: isMobile ? 280 : sidebarWidth,
        }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          background: 'var(--glass-bg-strong)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid var(--border-glass)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 300,
        }}
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Sidebar Header */}
        <div style={{ 
          padding: sidebarCollapsed ? '28px 16px' : '28px 24px', 
          borderBottom: '1px solid var(--border-faint)',
          flexShrink: 0,
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: sidebarCollapsed ? 'column' : 'row',
            alignItems: 'center', 
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            gap: sidebarCollapsed ? '16px' : '14px' 
          }}>
            {/* Logo Icon */}
            <div
              className="landing-nav__logo-icon"
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, var(--accent) 0%, var(--secondary) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: 'var(--sha-accent)',
                flexShrink: 0,
                position: 'relative',
              }}
            >
              <Sparkles size={20} />
            </div>

            {/* Brand Text */}
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  style={{ flex: 1 }}
                >
                  <div style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.4rem',
                    fontWeight: 800,
                    color: 'var(--text-primary)',
                    letterSpacing: '-0.01em',
                  }}>
                    LibasTrack
                  </div>
                  <div style={{
                    fontSize: '0.65rem',
                    color: 'var(--accent)',
                    letterSpacing: '0.2em',
                    textTransform: 'uppercase',
                    fontWeight: 700,
                    marginTop: '2px',
                  }}>
                    Brand OS
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Theme Toggle Button */}
            <button
              onClick={toggleTheme}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: isDark ? 'var(--accent-soft)' : 'var(--gold-soft)',
                border: `1px solid ${isDark ? 'var(--accent-border)' : 'rgba(217, 119, 6, 0.25)'}`,
                color: isDark ? 'var(--accent)' : 'var(--gold)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                flexShrink: 0,
              }}
              title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={theme}
                  initial={{ opacity: 0, rotate: -90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0, rotate: 90 }}
                  transition={{ duration: 0.2 }}
                >
                  {isDark ? <Sun size={18} /> : <Moon size={18} />}
                </motion.div>
              </AnimatePresence>
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '20px 12px',
          scrollbarWidth: 'none',
        }}>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            {NAV_GROUPS.map((group) => (
              <div key={group.section} style={{ marginBottom: '24px' }}>
                {!sidebarCollapsed && (
                  <motion.div
                    variants={staggerItem}
                    style={{
                      fontSize: '0.65rem',
                      color: 'var(--text-faint)',
                      letterSpacing: '0.2em',
                      textTransform: 'uppercase',
                      fontWeight: 700,
                      padding: '0 12px 10px',
                    }}
                  >
                    {group.section}
                  </motion.div>
                )}
                
                {group.items.map((item) => (
                  <motion.div key={item.to} variants={staggerItem}>
                    <NavLink
                      to={item.to}
                      style={({ isActive }) => ({
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: sidebarCollapsed ? '12px' : '12px 14px',
                        borderRadius: '10px',
                        fontSize: '0.9rem',
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                        background: isActive ? 'var(--accent-soft)' : 'transparent',
                        marginBottom: '4px',
                        transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                        textDecoration: 'none',
                      })}
                      title={sidebarCollapsed ? item.label : undefined}
                    >
                      {({ isActive }) => (
                        <>
                          {/* Active indicator */}
                          <div
                            style={{
                              position: 'absolute',
                              left: 0,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              height: isActive ? '60%' : '0%',
                              width: '3px',
                              background: 'var(--accent)',
                              borderRadius: '0 4px 4px 0',
                              opacity: isActive ? 1 : 0,
                              transition: 'height 0.3s ease, opacity 0.3s ease',
                            }}
                          />
                          
                          <span 
                            style={{ 
                              display: 'flex',
                              width: '20px',
                              height: '20px',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {item.icon}
                          </span>
                          
                          {!sidebarCollapsed && (
                            <span>{item.label}</span>
                          )}
                        </>
                      )}
                    </NavLink>
                  </motion.div>
                ))}
              </div>
            ))}
          </motion.div>
        </nav>

        {/* Sidebar Footer */}
        <div style={{ 
          padding: '16px 12px 24px', 
          borderTop: '1px solid var(--border-faint)',
          flexShrink: 0,
        }}>
          <SyncBadge />
          
          {/* User Card */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
            gap: sidebarCollapsed ? 0 : '12px',
            padding: sidebarCollapsed ? '8px' : '12px',
            borderRadius: '10px',
            background: 'var(--bg-layer1)',
            border: '1px solid var(--border-faint)',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #06b6d4 0%, #a855f7 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '1rem',
              color: 'white',
              flexShrink: 0,
              overflow: 'hidden',
            }}>
              {user?.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user.name} 
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                (user?.brand?.name || user?.name)?.[0]?.toUpperCase()
              )}
            </div>
            
            {!sidebarCollapsed && (
              <>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {user?.brand?.name || user?.name}
                  </div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-muted)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {user?.email}
                  </div>
                </div>
                
                <button
                  onClick={handleLogout}
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--text-muted)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  title="Sign out"
                >
                  <LogOut size={18} />
                </button>
              </>
            )}
          </div>

          {/* Collapse Toggle (Desktop only) */}
          {!isMobile && (
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{
                width: '100%',
                marginTop: '12px',
                padding: '10px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                color: 'var(--text-muted)',
                background: 'none',
                border: '1px solid var(--border-faint)',
                cursor: 'pointer',
                fontSize: '0.8rem',
                transition: 'background 0.2s',
              }}
            >
              {sidebarCollapsed ? (
                <ChevronRight size={16} />
              ) : (
                <>
                  <ChevronLeft size={16} />
                  <span>Collapse</span>
                </>
              )}
            </button>
          )}
        </div>
      </motion.nav>

      {/* Main Content */}
      <main
        style={{
          marginLeft: isMobile ? 0 : sidebarWidth,
          flex: 1,
          minHeight: '100vh',
          background: 'var(--bg-base)',
          transition: 'margin-left 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        role="main"
      >
        {/* Mobile Top Bar */}
        {isMobile && (
          <header style={{
            position: 'sticky',
            top: 0,
            left: 0,
            right: 0,
            height: '64px',
            padding: '0 20px',
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid var(--border-faint)',
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <button
              onClick={() => setSidebarOpen(true)}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-primary)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              aria-label="Open menu"
            >
              <Menu size={22} />
            </button>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontFamily: 'var(--font-display)',
              fontSize: '1.2rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
            }}>
              <div
                className="landing-nav__logo-icon"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, var(--accent) 0%, var(--secondary) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: 'var(--sha-accent)',
                  flexShrink: 0,
                }}
              >
                <Sparkles size={16} />
              </div>
              LibasTrack
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Mobile Theme Toggle */}
              <button
                onClick={toggleTheme}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: isDark ? 'var(--accent-soft)' : 'var(--gold-soft)',
                  border: `1px solid ${isDark ? 'var(--accent-border)' : 'rgba(217, 119, 6, 0.25)'}`,
                  color: isDark ? 'var(--accent)' : 'var(--gold)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                aria-label={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>

              <button
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
              >
                <Bell size={18} />
              </button>
            </div>
          </header>
        )}

        {/* Page Content with Transitions */}
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>

        {/* Mobile Bottom Navigation */}
        {isMobile && !sidebarOpen && (
          <nav style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            height: '72px',
            background: 'var(--glass-bg-strong)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid var(--border-faint)',
            zIndex: 300,
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-around',
              height: '100%',
              padding: '0 8px',
            }}>
              {MOBILE_NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '8px 12px',
                      color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                      fontSize: '0.65rem',
                      fontWeight: 500,
                      textDecoration: 'none',
                      position: 'relative',
                    }}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="mobile-nav-indicator"
                        style={{
                          position: 'absolute',
                          top: 0,
                          width: '24px',
                          height: '3px',
                          background: 'var(--accent)',
                          borderRadius: '0 0 4px 4px',
                        }}
                      />
                    )}
                    <span style={{ width: '24px', height: '24px' }}>
                      {item.icon}
                    </span>
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </nav>
        )}
      </main>
    </div>
  );
}
